import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { FaFacebook, FaYoutube, FaTimes } from "react-icons/fa";
import Login from "../pages/Login";
import Signup from "../pages/Signup";
import ChangePassword from "../pages/ChangePassword";
import "../styles/Header.css";

const CATEGORIES = [
  "Home", "Updates", "Education", "Entertainment", 
  "Insights", "Technology", "Business", "Health", 
  "Sports", "Stories"
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [authPopup, setAuthPopup] = useState({ open: false, mode: "login" });
  
  const userMenuRef = useRef(null);
  const isArticlePage = location.pathname.startsWith("/article/");

  // Event handlers
  const handleGoBack = () => navigate(-1);
  const openYouTubeChannel = () => window.open("https://www.youtube.com/@Readers_Menu", "_blank");
  const openFacebookPage = () => window.open("https://www.facebook.com/share/16nAvkgG7q/", "_blank");
  
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const closeMobileMenu = () => setMobileMenuOpen(false);
  
  const toggleUserMenu = () => setUserMenuOpen(!userMenuOpen);
  const closeUserMenu = () => setUserMenuOpen(false);
  
  const handleLogout = () => {
    logout();
    navigate("/");
    closeMobileMenu();
    closeUserMenu();
  };

  const handleAuthAction = (mode) => {
    setAuthPopup({ open: true, mode });
    closeUserMenu();
  };

  const closeAuthPopup = () => setAuthPopup({ open: false, mode: "login" });

  const switchToSignup = () => setAuthPopup(prev => ({ ...prev, mode: "signup" }));
  const switchToLogin = () => setAuthPopup(prev => ({ ...prev, mode: "login" }));
  const switchToChangePassword = () => setAuthPopup(prev => ({ ...prev, mode: "changePassword" }));

  const handleAuthSuccess = () => {
    closeAuthPopup();
    if (userMenuOpen) closeUserMenu();
  };

  // Effects
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        closeUserMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Components
  const UserMenuDropdown = () => (
    <div className="user-menu-dropdown">
      <div className="user-info">
        <div className={`user-avatar ${!user ? 'guest' : ''}`}>
          {user ? user.name?.charAt(0).toUpperCase() : 'G'}
        </div>
        <div className="user-details">
          <p className="user-name">{user?.name || "Guest User"}</p>
          <p className="user-email">{user?.email || "Sign in to access all features"}</p>
        </div>
      </div>
      <div className="user-menu-actions">
        {user ? (
          <>
            <button onClick={() => navigate("/forgot-password")} className="btn-secondary">
              Change Password
            </button>
            <button onClick={handleLogout} className="btn-danger">
              Logout
            </button>
          </>
        ) : (
          <>
            <button onClick={() => handleAuthAction("login")} className="btn-primary">
              Login
            </button>
            <button onClick={() => handleAuthAction("signup")} className="btn-outline">
              Create Account
            </button>
          </>
        )}
      </div>
    </div>
  );

  const MobileMenu = () => (
    <>
      <nav className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-header">
          <span>Menu</span>
          <button onClick={closeMobileMenu} className="close-menu" aria-label="Close menu">
            <FaTimes />
          </button>
        </div>
        <div className="theme-toggle-mobile">
          <label className="toggle-label">
            <input 
              type="checkbox" 
              checked={theme === "dark"} 
              onChange={toggleTheme}
              className="toggle-input"
            />
            <span className="toggle-slider"></span>
            <span className="toggle-text">{theme === "light" ? "Light Mode" : "Dark Mode"}</span>
          </label>
        </div>
        <div className="mobile-categories">
          {CATEGORIES.map((category) => (
            <NavLink
              key={category}
              to={category === "Home" ? "/" : `/${category}`}
              onClick={closeMobileMenu}
              className={({ isActive }) => `mobile-category ${isActive ? 'active' : ''}`}
            >
              {category}
            </NavLink>
          ))}
        </div>
        <div className="mobile-auth-buttons">
          {!user ? (
            <>
              <button onClick={() => handleAuthAction("login")} className="btn-primary full-width">
                Sign In
              </button>
              <button onClick={() => handleAuthAction("signup")} className="btn-outline full-width">
                Create Account
              </button>
            </>
          ) : (
            <>
              <div className="mobile-user-info">
                <span>Welcome, {user.name || "User"}!</span>
              </div>
              <button onClick={() => navigate("/forgot-password")} className="btn-secondary full-width">
                Change Password
              </button>
              <button onClick={handleLogout} className="btn-danger full-width">
                Logout
              </button>
            </>
          )}
        </div>
      </nav>
      {mobileMenuOpen && <div className="backdrop" onClick={closeMobileMenu} />}
    </>
  );

  const AuthModalContent = () => {
    switch (authPopup.mode) {
      case "login":
        return (
          <Login
            isPopup={true}
            onClose={closeAuthPopup}
            onSwitchToSignup={switchToSignup}
            onSwitchToChangePassword={switchToChangePassword}
            onSuccess={handleAuthSuccess}
          />
        );
      case "signup":
        return (
          <Signup
            isPopup={true}
            onClose={closeAuthPopup}
            onSwitchToLogin={switchToLogin}
            onSuccess={handleAuthSuccess}
          />
        );
      case "changePassword":
        return (
          <ChangePassword
            isPopup={true}
            onClose={closeAuthPopup}
            onSwitchToLogin={switchToLogin}
            onSuccess={handleAuthSuccess}
          />
        );
      default:
        return null;
    }
  };

  const AuthModal = () => (
    <div className="modal-overlay" onClick={closeAuthPopup}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {authPopup.mode === "login" && "Sign In"}
            {authPopup.mode === "signup" && "Create Account"}
            {authPopup.mode === "changePassword" && "Change Password"}
          </h3>
          <button onClick={closeAuthPopup} className="modal-close-btn" aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <div className="modal-body">
          <AuthModalContent />
        </div>
      </div>
    </div>
  );

  if (isArticlePage) {
    return (
      <div className="article-header">
        <button onClick={handleGoBack} className="back-btn">
          ← Back
        </button>
      </div>
    );
  }

  return (
    <>
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-top-bar">
          <div className="date">
            {new Date().toLocaleDateString("en-US", { 
              weekday: 'long', 
              month: "long", 
              day: "numeric", 
              year: "numeric" 
            })}
          </div>
          
          <NavLink to="/" className="logo-link">
            <img src="/images/Logo1.png" alt="Website Logo" className="logo-img" />
          </NavLink>
          
          <div className="header-actions">
            <button 
              onClick={toggleTheme} 
              className="icon-btn theme-toggle" 
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === "light" ? (
                <svg className="moon-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1-8.313-12.454z" />
                </svg>
              ) : (
                <svg className="sun-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 18a6 6 0 1 1 0-12 6 6 0 0 1 0 12zm0-2a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM11 1h2v3h-2V1zm0 19h2v3h-2v-3zM3.515 4.929l1.414-1.414L7.05 5.636 5.636 7.05 3.515 4.93zM16.95 18.364l1.414-1.414 2.121 2.121-1.414 1.414-2.121-2.121zm2.121-14.85l1.414 1.415-2.121 2.121-1.414-1.414 2.121-2.121zM5.636 16.95l1.414 1.414-2.121 2.121-1.414-1.414 2.121-2.121zM23 11v2h-3v-2h3zM4 11v2H1v-2h3z" />
                </svg>
              )}
            </button>
            <button onClick={openFacebookPage} className="icon-btn social" aria-label="Facebook">
              <FaFacebook />
            </button>
            <button onClick={openYouTubeChannel} className="icon-btn social youtube" aria-label="YouTube">
              <FaYoutube />
            </button>
            <div className="user-menu-wrapper" ref={userMenuRef}>
              <button onClick={toggleUserMenu} className="icon-btn user-btn" aria-label="Account menu">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm9 11a1 1 0 0 1-2 0v-2a3 3 0 0 0-3-3H8a3 3 0 0 0-3 3v2a1 1 0 0 1-2 0v-2a5 5 0 0 1 5-5h8a5 5 0 0 1 5 5v2z" />
                </svg>
              </button>
              {userMenuOpen && <UserMenuDropdown />}
            </div>
            <button 
              onClick={toggleMobileMenu} 
              className={`menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>

        <nav className="categories-nav">
          <div className="categories-scroll-container">
            {CATEGORIES.map((category) => (
              <NavLink
                key={category}
                to={category === "Home" ? "/" : `/${category}`}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                {category}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <MobileMenu />
      {authPopup.open && <AuthModal />}
    </>
  );
};

export default Header;